import { useCallback, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import Modal from "../../components/Modal";
import {
  deleteItemGroup,
  listItemGroups,
  upsertItemGroup,
  type ItemGroupRecord,
} from "../../services/catalog";

const itemGroupSchema = z.object({
  name: z.string().min(2, "Grup adı zorunludur"),
  slug: z
    .string()
    .min(2, "Slug zorunludur")
    .regex(/^[a-z0-9-]+$/, "Küçük harf, rakam veya tire kullanın"),
  description: z.string().optional(),
  displayOrder: z.coerce.number().min(0),
  isActive: z.boolean().default(true),
});

type ItemGroupFormValues = z.infer<typeof itemGroupSchema>;

const DEFAULT_VALUES: ItemGroupFormValues = {
  name: "",
  slug: "",
  description: "",
  displayOrder: 0,
  isActive: true,
};

const autoSlug = (name: string) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)+/g, "");

export default function ItemGroupManager() {
  const queryClient = useQueryClient();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ItemGroupRecord | null>(null);

  const {
    data: groups = [],
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["catalog", "item-groups"],
    queryFn: listItemGroups,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ItemGroupFormValues>({
    resolver: zodResolver(itemGroupSchema),
    defaultValues: DEFAULT_VALUES,
  });

  const upsertMutation = useMutation({
    mutationFn: upsertItemGroup,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog", "item-groups"] });
      closeModal();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (group: ItemGroupRecord) => deleteItemGroup(group.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["catalog", "item-groups"] });
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : "Grup silinirken bir hata oluştu. Lütfen tekrar deneyin.";
      window.alert(message);
    },
  });

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingGroup(null);
    reset(DEFAULT_VALUES);
  }, [reset]);

  const openCreate = () => {
    setEditingGroup(null);
    reset(DEFAULT_VALUES);
    setModalOpen(true);
  };

  const openEdit = (group: ItemGroupRecord) => {
    setEditingGroup(group);
    reset({
      name: group.name,
      slug: group.slug,
      description: group.description ?? "",
      displayOrder: group.displayOrder,
      isActive: group.isActive,
    });
    setModalOpen(true);
  };

  const onSubmit = (values: ItemGroupFormValues) => {
    const payload = {
      id: editingGroup?.id,
      name: values.name,
      slug: values.slug,
      description: values.description ?? "",
      displayOrder: Number(values.displayOrder),
      isActive: values.isActive,
    };
    return upsertMutation.mutateAsync(payload);
  };

  const handleNameBlur = () => {
    const name = watch("name");
    if (!editingGroup && name && !watch("slug")) {
      setValue("slug", autoSlug(name));
    }
  };

  return (
    <>
      <div className="card">
        <div className="card-header">
          <div>
            <p className="card-title">Item groups</p>
            <p className="card-subtitle">
              {isLoading || isFetching
                ? "Yükleniyor..."
                : `Toplam ${groups.length} grup listeleniyor`}
            </p>
          </div>
          <button type="button" className="button primary" onClick={openCreate}>
            + Grup Ekle
          </button>
        </div>

        {groups.length === 0 ? (
          <div className="empty-state">
            <strong>Henüz grup yok</strong>
            <p>Kategorileri düzenlemek için önce item group oluşturun.</p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sıra</th>
                  <th>Ad</th>
                  <th>Slug</th>
                  <th>Durum</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {groups.map((group) => (
                  <tr key={group.id}>
                    <td>{group.displayOrder}</td>
                    <td>
                      <strong>{group.name}</strong>
                      {group.description ? (
                        <p className="helper-text">{group.description}</p>
                      ) : null}
                    </td>
                    <td>{group.slug}</td>
                    <td>
                      <span className={`chip ${group.isActive ? "success" : "warning"}`}>
                        {group.isActive ? "Yayında" : "Gizli"}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="button secondary"
                          onClick={() => openEdit(group)}
                        >
                          Düzenle
                        </button>
                        <button
                          type="button"
                          className="button danger"
                          onClick={() => {
                            if (
                              window.confirm(
                                `${group.name} grubunu silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`,
                              )
                            ) {
                              deleteMutation.mutate(group);
                            }
                          }}
                        >
                          Sil
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingGroup ? `${editingGroup.name} düzenleniyor` : "Yeni grup ekle"}
      >
        <form className="form-grid" onSubmit={handleSubmit(onSubmit)}>
          <label className="form-field">
            <span>Ad</span>
            <input
              type="text"
              placeholder="Örn. Pizzalar"
              {...register("name")}
              onBlur={handleNameBlur}
            />
            {errors.name ? <p className="error-text">{errors.name.message}</p> : null}
          </label>

          <label className="form-field">
            <span>Slug</span>
            <input type="text" placeholder="pizzalar" {...register("slug")} />
            {errors.slug ? <p className="error-text">{errors.slug.message}</p> : null}
          </label>

          <label className="form-field">
            <span>Açıklama</span>
            <textarea
              placeholder="Bu grup için kısa bir açıklama"
              rows={3}
              {...register("description")}
            />
            {errors.description ? (
              <p className="error-text">{errors.description.message}</p>
            ) : null}
          </label>

          <label className="form-field">
            <span>Sıra</span>
            <input type="number" min={0} {...register("displayOrder")} />
            {errors.displayOrder ? (
              <p className="error-text">{errors.displayOrder.message}</p>
            ) : null}
          </label>

          <label className="form-field checkbox inline-checkbox">
            <input type="checkbox" {...register("isActive")} />
            <span>Aktif</span>
          </label>

          <div className="form-actions">
            <button type="button" className="button ghost" onClick={closeModal}>
              İptal
            </button>
            <button className="button primary" type="submit" disabled={isSubmitting}>
              {editingGroup ? "Grubu güncelle" : "Grup ekle"}
            </button>
          </div>
        </form>
      </Modal>
    </>
  );
}
